import React from 'react';
import DateTimeFormatHelper from '@shared/common/DateTimeFormatHelper';
import {
    KNOWLEDGE_LIBRARY_CARD_ZOOM_DEFAULT_PERCENT,
    KNOWLEDGE_LIBRARY_CARD_ZOOM_MAX_PERCENT,
    KNOWLEDGE_LIBRARY_CARD_ZOOM_MIN_PERCENT,
    KNOWLEDGE_LIBRARY_CARD_ZOOM_STEP_PERCENT,
    KNOWLEDGE_LIBRARY_KV_CARD_ZOOM_SESSION_KEY,
    loadKnowledgeLibraryCardZoomPercent,
    saveKnowledgeLibraryCardZoomPercent,
} from './knowledgeLibraryCardZoomSession';

interface KnowledgeViewsDrawerProps {
    isOpen: boolean;
    knowledgeBookName: string;
    knowledgeViews: IKnowledgeView[];
    isLoading: boolean;
    boundsRef: React.RefObject<HTMLElement>;
    onOpenKnowledgeView: (kv: IKnowledgeView) => void | Promise<void>;
    onClose: () => void;
    isSidebarOpen?: boolean;
}

const KNOWLEDGE_VIEW_PLACEHOLDER_IMAGE = '/images/KB/KVLarge.png';
const KNOWLEDGE_VIEW_TYPE_3D = 2;
const DEFAULT_DRAWER_HEIGHT = 255;
const MIN_DRAWER_HEIGHT = DEFAULT_DRAWER_HEIGHT;
const MAX_DRAWER_VIEWPORT_RATIO = 0.85;
const KNOWLEDGE_VIEW_CARD_BASE_WIDTH = 190;
const KNOWLEDGE_VIEW_CARD_BASE_HEIGHT = (KNOWLEDGE_VIEW_CARD_BASE_WIDTH * 9 / 16) + 2;
interface DrawerBounds {
    left: number;
    width: number;
}

const KnowledgeViewsDrawer: React.FC<KnowledgeViewsDrawerProps> = ({
    isOpen,
    knowledgeBookName,
    knowledgeViews,
    isLoading,
    boundsRef,
    onOpenKnowledgeView,
    onClose,
    isSidebarOpen
}) => {
    const [height, setHeight] = React.useState<number>(DEFAULT_DRAWER_HEIGHT);
    const [isMinimized, setIsMinimized] = React.useState<boolean>(false);
    const [revealedKnowledgeViewId, setRevealedKnowledgeViewId] = React.useState<string | null>(null);
    const [bounds, setBounds] = React.useState<DrawerBounds>({ left: 0, width: 0 });
    const [searchText, setSearchText] = React.useState<string>('');
    const [cardZoomPercent, setCardZoomPercent] = React.useState<number>(() => loadKnowledgeLibraryCardZoomPercent(KNOWLEDGE_LIBRARY_KV_CARD_ZOOM_SESSION_KEY));
    const [fullPageKnowledgeViewId, setFullPageKnowledgeViewId] = React.useState<string | null>(null);
    const restoreHeightRef = React.useRef<number>(DEFAULT_DRAWER_HEIGHT);
    const restoreFullPageHeightRef = React.useRef<number>(DEFAULT_DRAWER_HEIGHT);
    const dragStateRef = React.useRef<{ startY: number; startHeight: number } | null>(null);
    const cardScale = cardZoomPercent / 100;
    const descriptionLineClamp = Math.max(2, Math.round(3 * cardScale));

    const getMaxHeight = React.useCallback(() => Math.round(window.innerHeight * MAX_DRAWER_VIEWPORT_RATIO), []);

    const updateBounds = React.useCallback(() => {
        const element = boundsRef.current;
        if (!element) {
            return;
        }
        const rect = element.getBoundingClientRect();
        setBounds({ left: rect.left, width: rect.width });
    }, [boundsRef]);

    React.useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        updateBounds();

        const element = boundsRef.current;
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updateBounds()) : null;
        if (resizeObserver && element) {
            resizeObserver.observe(element);
        }

        window.addEventListener('resize', updateBounds);
        window.addEventListener('scroll', updateBounds, true);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateBounds);
            window.removeEventListener('scroll', updateBounds, true);
        };
    }, [boundsRef, isOpen, updateBounds]);

    React.useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        const refreshBoundsAfterSidebarTransition = (event: TransitionEvent) => {
            if (event.target === event.currentTarget) {
                updateBounds();
            }
        };

        const sidebarElement = document.getElementById('sidebar');
        const appContainerElement = document.querySelector('.facility-app-container');

        sidebarElement?.addEventListener('transitionend', refreshBoundsAfterSidebarTransition);
        sidebarElement?.addEventListener('transitioncancel', refreshBoundsAfterSidebarTransition);
        appContainerElement?.addEventListener('transitionend', refreshBoundsAfterSidebarTransition);
        appContainerElement?.addEventListener('transitioncancel', refreshBoundsAfterSidebarTransition);

        return () => {
            sidebarElement?.removeEventListener('transitionend', refreshBoundsAfterSidebarTransition);
            sidebarElement?.removeEventListener('transitioncancel', refreshBoundsAfterSidebarTransition);
            appContainerElement?.removeEventListener('transitionend', refreshBoundsAfterSidebarTransition);
            appContainerElement?.removeEventListener('transitioncancel', refreshBoundsAfterSidebarTransition);
        };
    }, [isOpen, isSidebarOpen, updateBounds]);

    React.useEffect(() => {
        setSearchText('');
        setRevealedKnowledgeViewId(null);
        setFullPageKnowledgeViewId(null);
    }, [isOpen, knowledgeBookName]);

    const handleResizeMove = React.useCallback((event: MouseEvent) => {
        const dragState = dragStateRef.current;
        if (!dragState) {
            return;
        }

        const delta = dragState.startY - event.clientY;
        const nextHeight = Math.min(Math.max(dragState.startHeight + delta, MIN_DRAWER_HEIGHT), getMaxHeight());
        setHeight(nextHeight);
    }, [getMaxHeight]);

    const stopResize = React.useCallback(() => {
        dragStateRef.current = null;
        document.body.classList.remove('knowledge-views-drawer-resizing');
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', stopResize);
    }, [handleResizeMove]);

    const handleResizeStart = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (isMinimized || fullPageKnowledgeViewId) {
            return;
        }

        event.preventDefault();
        dragStateRef.current = { startY: event.clientY, startHeight: height };
        document.body.classList.add('knowledge-views-drawer-resizing');
        document.addEventListener('mousemove', handleResizeMove);
        document.addEventListener('mouseup', stopResize);
    }, [fullPageKnowledgeViewId, handleResizeMove, height, isMinimized, stopResize]);

    React.useEffect(() => {
        return () => {
            document.body.classList.remove('knowledge-views-drawer-resizing');
            document.removeEventListener('mousemove', handleResizeMove);
            document.removeEventListener('mouseup', stopResize);
        };
    }, [handleResizeMove, stopResize]);

    const handleToggleMinimize = React.useCallback(() => {
        if (fullPageKnowledgeViewId) {
            setFullPageKnowledgeViewId(null);
            setHeight(restoreFullPageHeightRef.current);
            return;
        }

        setIsMinimized(previous => {
            if (!previous) {
                restoreHeightRef.current = height;
                return true;
            }

            setHeight(restoreHeightRef.current);
            return false;
        });
    }, [fullPageKnowledgeViewId, height]);

    const handleZoomChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setCardZoomPercent(saveKnowledgeLibraryCardZoomPercent(KNOWLEDGE_LIBRARY_KV_CARD_ZOOM_SESSION_KEY, Number(event.target.value)));
    }, []);

    const handleZoomStep = React.useCallback((delta: number) => {
        setCardZoomPercent(previous => saveKnowledgeLibraryCardZoomPercent(KNOWLEDGE_LIBRARY_KV_CARD_ZOOM_SESSION_KEY, previous + delta));
    }, []);

    const handleZoomReset = React.useCallback(() => {
        if (fullPageKnowledgeViewId) {
            setFullPageKnowledgeViewId(null);
            setHeight(restoreFullPageHeightRef.current);
        }

        setCardZoomPercent(saveKnowledgeLibraryCardZoomPercent(KNOWLEDGE_LIBRARY_KV_CARD_ZOOM_SESSION_KEY, KNOWLEDGE_LIBRARY_CARD_ZOOM_DEFAULT_PERCENT));
    }, [fullPageKnowledgeViewId]);

    const filteredKnowledgeViews = React.useMemo(() => {
        const normalizedSearchText = searchText.trim().toLowerCase();
        if (!normalizedSearchText) {
            return knowledgeViews;
        }

        return knowledgeViews.filter(kv => kv.Name.toLowerCase().includes(normalizedSearchText));
    }, [knowledgeViews, searchText]);

    const formatModifiedDate = React.useCallback((modifiedDate?: string) => {
        if (!modifiedDate) {
            return '';
        }

        return `${DateTimeFormatHelper.getFormatDate(modifiedDate)} ${DateTimeFormatHelper.getFormatTime(modifiedDate)}`;
    }, []);

    React.useEffect(() => {
        const visibleKnowledgeViewIds = new Set(filteredKnowledgeViews.map(kv => kv.ID));
        setRevealedKnowledgeViewId(previous => {
            if (!previous || visibleKnowledgeViewIds.has(previous)) {
                return previous;
            }

            return null;
        });
        setFullPageKnowledgeViewId(previous => {
            if (!previous || visibleKnowledgeViewIds.has(previous)) {
                return previous;
            }

            return null;
        });
    }, [filteredKnowledgeViews]);

    const enterFullPageMode = React.useCallback((knowledgeViewId: string) => {
        restoreFullPageHeightRef.current = height;
        setIsMinimized(false);
        setRevealedKnowledgeViewId(null);
        setFullPageKnowledgeViewId(knowledgeViewId);
        setHeight(getMaxHeight());
    }, [getMaxHeight, height]);

    const fullPageKnowledgeViewIndex = fullPageKnowledgeViewId
        ? filteredKnowledgeViews.findIndex(kv => kv.ID === fullPageKnowledgeViewId)
        : -1;
    const fullPageKnowledgeView = fullPageKnowledgeViewIndex >= 0 ? filteredKnowledgeViews[fullPageKnowledgeViewIndex] : null;
    const hasPreviousFullPageKnowledgeView = fullPageKnowledgeViewIndex > 0;
    const hasNextFullPageKnowledgeView = fullPageKnowledgeViewIndex >= 0 && fullPageKnowledgeViewIndex < filteredKnowledgeViews.length - 1;
    const fullPageCardScale = fullPageKnowledgeView
        ? Math.max(1, Math.min(
            Math.max(bounds.width - 96, KNOWLEDGE_VIEW_CARD_BASE_WIDTH) / KNOWLEDGE_VIEW_CARD_BASE_WIDTH,
            Math.max(height - 104, KNOWLEDGE_VIEW_CARD_BASE_HEIGHT) / KNOWLEDGE_VIEW_CARD_BASE_HEIGHT
        ))
        : 1;

    const moveFullPageSelection = React.useCallback((delta: number) => {
        if (fullPageKnowledgeViewIndex < 0) {
            return;
        }

        const nextKnowledgeView = filteredKnowledgeViews[fullPageKnowledgeViewIndex + delta];
        if (nextKnowledgeView) {
            setFullPageKnowledgeViewId(nextKnowledgeView.ID);
        }
    }, [filteredKnowledgeViews, fullPageKnowledgeViewIndex]);

    if (!isOpen) {
        return null;
    }

    const drawerStyle: React.CSSProperties = {
        left: `${bounds.left}px`,
        width: `${bounds.width}px`,
        height: isMinimized ? undefined : `${height}px`
    };

    const title = InovxLoader.t('facility:knowledgeBook.knowledgeViewsTitle', {
        name: knowledgeBookName,
        count: knowledgeViews.length
    });

    return (
        <div
            className={isMinimized ? 'knowledge-views-drawer is-minimized' : fullPageKnowledgeView ? 'knowledge-views-drawer is-full-page' : 'knowledge-views-drawer'}
            style={drawerStyle}
            role="dialog"
            aria-label={title}
        >
            {!isMinimized && !fullPageKnowledgeView && (
                <div
                    className="knowledge-views-drawer-resize-handle"
                    onMouseDown={handleResizeStart}
                    role="separator"
                    aria-orientation="horizontal"
                    aria-label={InovxLoader.t('facility:knowledgeBook.resizeDrawer')}
                />
            )}
            <div className="knowledge-views-drawer-header">
                <div className="knowledge-views-drawer-title-shell" title={title}>
                    <span className="kb-icon kb-book-icon" aria-hidden="true"></span>
                    <span className="knowledge-views-drawer-title">
                        <span className="knowledge-views-drawer-title-kb">{knowledgeBookName}</span>
                        <span className="knowledge-views-drawer-title-meta"> – {InovxLoader.t('facility:knowledgeBook.knowledgeViewsLabel', { count: knowledgeViews.length })}</span>
                    </span>
                </div>
                {!isMinimized && (
                    <div className="knowledge-views-drawer-controls">
                        <div className="knowledge-views-drawer-search e-input-group">
                            <input
                                className="e-input"
                                type="text"
                                value={searchText}
                                placeholder={InovxLoader.t('facility:knowledgeBook.searchKnowledgeViews')}
                                aria-label={InovxLoader.t('facility:knowledgeBook.searchKnowledgeViews')}
                                onChange={(event) => setSearchText(event.target.value)}
                            />
                            {searchText && (
                                <button
                                    type="button"
                                    className="knowledge-views-drawer-search-clear"
                                    title={InovxLoader.t('common:clear')}
                                    aria-label={InovxLoader.t('common:clear')}
                                    onClick={() => setSearchText('')}
                                >
                                    <span className="e-icons e-close" aria-hidden="true"></span>
                                </button>
                            )}
                            <span className="e-input-group-icon e-icons e-search" aria-hidden="true"></span>
                        </div>
                        <div className="knowledge-library-card-zoom knowledge-views-drawer-card-zoom" title={InovxLoader.t('facility:knowledgeBook.cardZoom')}>
                            <button
                                type="button"
                                className="knowledge-library-card-zoom-button"
                                title={InovxLoader.t('facility:knowledgeBook.cardZoomOut')}
                                aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomOut')}
                                onClick={() => handleZoomStep(-KNOWLEDGE_LIBRARY_CARD_ZOOM_STEP_PERCENT)}
                                disabled={!!fullPageKnowledgeView || cardZoomPercent <= KNOWLEDGE_LIBRARY_CARD_ZOOM_MIN_PERCENT}
                            >
                                <span className="fa fa-search-minus" aria-hidden="true"></span>
                            </button>
                            <input
                                type="range"
                                className="knowledge-library-card-zoom-slider"
                                min={KNOWLEDGE_LIBRARY_CARD_ZOOM_MIN_PERCENT}
                                max={KNOWLEDGE_LIBRARY_CARD_ZOOM_MAX_PERCENT}
                                step={KNOWLEDGE_LIBRARY_CARD_ZOOM_STEP_PERCENT}
                                value={cardZoomPercent}
                                onChange={handleZoomChange}
                                disabled={!!fullPageKnowledgeView}
                                aria-label={InovxLoader.t('facility:knowledgeBook.cardZoom')}
                            />
                            <button
                                type="button"
                                className="knowledge-library-card-zoom-button"
                                title={InovxLoader.t('facility:knowledgeBook.cardZoomIn')}
                                aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomIn')}
                                onClick={() => handleZoomStep(KNOWLEDGE_LIBRARY_CARD_ZOOM_STEP_PERCENT)}
                                disabled={!!fullPageKnowledgeView || cardZoomPercent >= KNOWLEDGE_LIBRARY_CARD_ZOOM_MAX_PERCENT}
                            >
                                <span className="fa fa-search-plus" aria-hidden="true"></span>
                            </button>
                            <button
                                type="button"
                                className="knowledge-library-card-zoom-value"
                                title={InovxLoader.t('facility:knowledgeBook.cardZoomReset')}
                                aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomReset')}
                                onClick={handleZoomReset}
                            >
                                {fullPageKnowledgeView ? InovxLoader.t('facility:knowledgeBook.cardZoomMaxValue') : `${cardZoomPercent}%`}
                            </button>
                        </div>
                    </div>
                )}
                <button
                    type="button"
                    className="e-card-btn knowledge-views-drawer-toggle"
                    title={fullPageKnowledgeView ? InovxLoader.t('facility:knowledgeBook.exitFullPageMode') : isMinimized ? InovxLoader.t('facility:knowledgeBook.expand') : InovxLoader.t('facility:knowledgeBook.minimize')}
                    onClick={handleToggleMinimize}
                >
                    <span className={isMinimized ? 'e-icons e-chevron-up' : 'e-icons e-chevron-down'} aria-hidden="true"></span>
                </button>
                <button
                    type="button"
                    className="e-card-btn knowledge-views-drawer-toggle knowledge-views-drawer-close"
                    title={InovxLoader.t('facility:knowledgeBook.closeDetails')}
                    aria-label={InovxLoader.t('facility:knowledgeBook.closeDetails')}
                    onClick={onClose}
                >
                    <span className="e-icons e-close" aria-hidden="true"></span>
                </button>
            </div>
            {!isMinimized && (
                <div className="knowledge-views-drawer-body">
                    {isLoading ? (
                        <div className="knowledge-views-drawer-message">{InovxLoader.t('facility:knowledgeBook.knowledgeViewsLoading')}</div>
                    ) : knowledgeViews.length === 0 ? (
                        <div className="knowledge-views-drawer-message">{InovxLoader.t('facility:knowledgeBook.knowledgeViewsEmpty')}</div>
                    ) : filteredKnowledgeViews.length === 0 ? (
                        <div className="knowledge-views-drawer-message">{InovxLoader.t('facility:knowledgeBook.knowledgeViewsNoSearchResults')}</div>
                    ) : fullPageKnowledgeView ? (() => {
                        const is3D = fullPageKnowledgeView.Type === KNOWLEDGE_VIEW_TYPE_3D;
                        const isRevealed = revealedKnowledgeViewId === fullPageKnowledgeView.ID;
                        const hasThumbnail = !!fullPageKnowledgeView.ThumbnailImageUrl;
                        const thumbnailUrl = fullPageKnowledgeView.ThumbnailImageUrl || KNOWLEDGE_VIEW_PLACEHOLDER_IMAGE;
                        const badgeLabel = is3D
                            ? InovxLoader.t('facility:knowledgeBook.knowledgeView3D')
                            : InovxLoader.t('facility:knowledgeBook.knowledgeView360');
                        const description = fullPageKnowledgeView.Description || InovxLoader.t('facility:knowledgeBook.noDescription');

                        return (
                            <div className="knowledge-views-drawer-full-page-stage">
                                <div
                                    className="knowledge-views-drawer-full-page-grid"
                                    style={{
                                        ['--klib-kv-card-scale' as string]: fullPageCardScale,
                                        ['--klib-card-description-line-clamp' as string]: Math.max(4, Math.round(5 * fullPageCardScale))
                                    }}
                                >
                                    <div
                                        className={isRevealed ? 'knowledge-views-drawer-item knowledge-views-drawer-item-full-page is-revealed' : 'knowledge-views-drawer-item knowledge-views-drawer-item-full-page'}
                                        title={fullPageKnowledgeView.Name}
                                    >
                                        <div className={isRevealed ? 'knowledge-views-drawer-item-face is-revealed' : 'knowledge-views-drawer-item-face'}>
                                            <button
                                                type="button"
                                                className="knowledge-views-drawer-thumb-button"
                                                onClick={() => setRevealedKnowledgeViewId(fullPageKnowledgeView.ID)}
                                            >
                                                <span className="knowledge-views-drawer-thumb">
                                                    {hasThumbnail ? (
                                                        <img src={thumbnailUrl} alt="" />
                                                    ) : (
                                                        <span className="kv-placeholder-image" style={{ backgroundImage: `url(${thumbnailUrl})` }} aria-hidden="true"></span>
                                                    )}
                                                    <span
                                                        className={is3D
                                                            ? 'knowledge-views-drawer-badge knowledge-views-drawer-badge-3d'
                                                            : 'knowledge-views-drawer-badge knowledge-views-drawer-badge-360'}
                                                        title={badgeLabel}
                                                    >
                                                        <span className={is3D ? 'fa fa-cube' : 'fa fa-globe'} aria-hidden="true"></span>
                                                        <span className="knowledge-views-drawer-badge-text">{badgeLabel}</span>
                                                    </span>
                                                </span>
                                            </button>
                                            <div className="knowledge-views-drawer-item-info">
                                                <div className="knowledge-views-drawer-item-name" title={fullPageKnowledgeView.Name}>{fullPageKnowledgeView.Name}</div>
                                                <button
                                                    type="button"
                                                    className="e-btn e-outline e-primary knowledge-views-drawer-open-button"
                                                    onClick={() => onOpenKnowledgeView(fullPageKnowledgeView)}
                                                >
                                                    {InovxLoader.t('facility:knowledgeBook.open')}
                                                </button>
                                            </div>
                                        </div>
                                        <div className={isRevealed ? 'knowledge-views-drawer-item-reveal-face is-revealed' : 'knowledge-views-drawer-item-reveal-face'}>
                                            <div className="knowledge-library-card-reveal-header-shell">
                                                <div className="knowledge-library-card-reveal-caption">
                                                    <span className="kb-icon kb-book-icon" aria-hidden="true"></span>
                                                    <div className="knowledge-library-card-reveal-title">{fullPageKnowledgeView.Name}</div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="knowledge-library-card-collapse"
                                                    title={InovxLoader.t('facility:knowledgeBook.closeDetails')}
                                                    onClick={() => setRevealedKnowledgeViewId(null)}
                                                >
                                                    <span className="e-icons e-close" aria-hidden="true"></span>
                                                </button>
                                            </div>
                                            <div className="knowledge-library-card-reveal-content knowledge-views-drawer-reveal-content">
                                                <div className="knowledge-library-card-reveal-text knowledge-library-card-reveal-description" dangerouslySetInnerHTML={{ __html: description }}></div>
                                                <div className="knowledge-library-card-reveal-section">
                                                    <div className="knowledge-library-card-reveal-label">{InovxLoader.t('facility:knowledgeBook.lastModifiedBy')}</div>
                                                    <div className="knowledge-library-card-reveal-text">{fullPageKnowledgeView.ModifiedByUser || ''}</div>
                                                </div>
                                                <div className="knowledge-library-card-reveal-section">
                                                    <div className="knowledge-library-card-reveal-label">{InovxLoader.t('facility:knowledgeBook.lastModifiedOn')}</div>
                                                    <div className="knowledge-library-card-reveal-text">{formatModifiedDate(fullPageKnowledgeView.ModifiedDate)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="knowledge-views-drawer-full-page-navigation" aria-label={InovxLoader.t('facility:knowledgeBook.fullPageNavigation')}>
                                    <button
                                        type="button"
                                        className="knowledge-views-drawer-full-page-nav-button"
                                        title={InovxLoader.t('facility:knowledgeBook.previousKnowledgeView')}
                                        aria-label={InovxLoader.t('facility:knowledgeBook.previousKnowledgeView')}
                                        onClick={() => moveFullPageSelection(-1)}
                                        disabled={!hasPreviousFullPageKnowledgeView}
                                    >
                                        <span className="fa fa-chevron-up" aria-hidden="true"></span>
                                    </button>
                                    <button
                                        type="button"
                                        className="knowledge-views-drawer-full-page-nav-button"
                                        title={InovxLoader.t('facility:knowledgeBook.nextKnowledgeView')}
                                        aria-label={InovxLoader.t('facility:knowledgeBook.nextKnowledgeView')}
                                        onClick={() => moveFullPageSelection(1)}
                                        disabled={!hasNextFullPageKnowledgeView}
                                    >
                                        <span className="fa fa-chevron-down" aria-hidden="true"></span>
                                    </button>
                                </div>
                            </div>
                        );
                    })()
                    : (
                        <div
                            className="knowledge-views-drawer-grid"
                            style={{
                                ['--klib-kv-card-scale' as string]: cardScale,
                                ['--klib-card-description-line-clamp' as string]: descriptionLineClamp
                            }}
                        >
                            {filteredKnowledgeViews.map(kv => {
                                const is3D = kv.Type === KNOWLEDGE_VIEW_TYPE_3D;
                                const isRevealed = revealedKnowledgeViewId === kv.ID;
                                const hasThumbnail = !!kv.ThumbnailImageUrl;
                                const thumbnailUrl = kv.ThumbnailImageUrl || KNOWLEDGE_VIEW_PLACEHOLDER_IMAGE;
                                const badgeLabel = is3D
                                    ? InovxLoader.t('facility:knowledgeBook.knowledgeView3D')
                                    : InovxLoader.t('facility:knowledgeBook.knowledgeView360');
                                const description = kv.Description || InovxLoader.t('facility:knowledgeBook.noDescription');

                                return (
                                    <div
                                        className={isRevealed ? 'knowledge-views-drawer-item is-revealed' : 'knowledge-views-drawer-item'}
                                        key={kv.ID}
                                        title={kv.Name}
                                    >
                                        {!isRevealed && (
                                            <button
                                                type="button"
                                                className="knowledge-views-drawer-expand-button"
                                                title={InovxLoader.t('facility:knowledgeBook.expandKnowledgeViewCard')}
                                                aria-label={InovxLoader.t('facility:knowledgeBook.expandKnowledgeViewCard')}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    enterFullPageMode(kv.ID);
                                                }}
                                            >
                                                <span className="fa fa-expand" aria-hidden="true"></span>
                                            </button>
                                        )}
                                        <div className={isRevealed ? 'knowledge-views-drawer-item-face is-revealed' : 'knowledge-views-drawer-item-face'}>
                                            <button
                                                type="button"
                                                className="knowledge-views-drawer-thumb-button"
                                                onClick={() => setRevealedKnowledgeViewId(kv.ID)}
                                            >
                                                <span className="knowledge-views-drawer-thumb">
                                                    {hasThumbnail ? (
                                                        <img src={thumbnailUrl} alt="" />
                                                    ) : (
                                                        <span className="kv-placeholder-image" style={{ backgroundImage: `url(${thumbnailUrl})` }} aria-hidden="true"></span>
                                                    )}
                                                    <span
                                                        className={is3D
                                                            ? 'knowledge-views-drawer-badge knowledge-views-drawer-badge-3d'
                                                            : 'knowledge-views-drawer-badge knowledge-views-drawer-badge-360'}
                                                        title={badgeLabel}
                                                    >
                                                        <span className={is3D ? 'fa fa-cube' : 'fa fa-globe'} aria-hidden="true"></span>
                                                        <span className="knowledge-views-drawer-badge-text">{badgeLabel}</span>
                                                    </span>
                                                </span>
                                            </button>
                                            <div className="knowledge-views-drawer-item-info">
                                                <div className="knowledge-views-drawer-item-name" title={kv.Name}>{kv.Name}</div>
                                                <button
                                                    type="button"
                                                    className="e-btn e-outline e-primary knowledge-views-drawer-open-button"
                                                    onClick={() => onOpenKnowledgeView(kv)}
                                                >
                                                    {InovxLoader.t('facility:knowledgeBook.open')}
                                                </button>
                                            </div>
                                        </div>
                                        <div className={isRevealed ? 'knowledge-views-drawer-item-reveal-face is-revealed' : 'knowledge-views-drawer-item-reveal-face'}>
                                            <div className="knowledge-library-card-reveal-header-shell">
                                                <div className="knowledge-library-card-reveal-caption">
                                                    <span className="kb-icon kb-book-icon" aria-hidden="true"></span>
                                                    <div className="knowledge-library-card-reveal-title">{kv.Name}</div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="knowledge-library-card-collapse"
                                                    title={InovxLoader.t('facility:knowledgeBook.closeDetails')}
                                                    onClick={() => setRevealedKnowledgeViewId(null)}
                                                >
                                                    <span className="e-icons e-close" aria-hidden="true"></span>
                                                </button>
                                            </div>
                                            <div className="knowledge-library-card-reveal-content knowledge-views-drawer-reveal-content">
                                                <div className="knowledge-library-card-reveal-text knowledge-library-card-reveal-description" dangerouslySetInnerHTML={{ __html: description }}></div>

                                                <div className="knowledge-library-card-reveal-section">
                                                    <div className="knowledge-library-card-reveal-label">{InovxLoader.t('facility:knowledgeBook.lastModifiedBy')}</div>
                                                    <div className="knowledge-library-card-reveal-text">{kv.ModifiedByUser || ''}</div>
                                                </div>
                                                <div className="knowledge-library-card-reveal-section">
                                                    <div className="knowledge-library-card-reveal-label">{InovxLoader.t('facility:knowledgeBook.lastModifiedOn')}</div>
                                                    <div className="knowledge-library-card-reveal-text">{formatModifiedDate(kv.ModifiedDate)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default KnowledgeViewsDrawer;
